import { useState } from 'react'
import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { History } from 'lucide-react'
import {
  Bidi, Card, CardBody, CardHeader, EmptyState, ErrorState, LoadingState,
  NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, fetchLedger, formatCount, formatDateTime, formatQuantity, may, nameIn,
  translateError, type LedgerRow,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'

/** The same `.replace` every screen in this folder does. */
const fill = (template: string, values: Record<string, string>) =>
  Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value), template)

const PAGE = 50

/* ============================================================================
   W-101 — HOW IT GOT TO FOUR.  Sprint 80.

   ── EVERY BALANCE IN THIS PRODUCT COMES FROM HERE AND NOTHING SHOWED IT ────────
   The stock board says the fourth floor pantry holds four bath towels. The replenishment
   list subtracts four from the par. The count variance compares four against what somebody
   found on the shelf. All three read the same ledger, and until this screen the only
   available answer to *"why does it say four?"* was **"because it does"** — which is the
   one answer a store keeper at a shelf and a manager in front of a variance cannot use.

   ── IT READS AND IT DOES NOT WRITE, BECAUSE THERE IS NOTHING TO WRITE ──────────
   The ledger is append-only everywhere in this product. There is no edit here and no
   delete, not as a restriction but because a correction **is a new movement in the other
   direction** — recorded on W-102 like any other. W-98 in the packs is a screen for
   reversing a movement and it does not exist for exactly this reason.

   ── THE TOTAL IS OF THE FILTER, NOT OF THE PAGE ────────────────────────────────
   A screen showing fifty rows out of four thousand and saying nothing answers *"how did it
   get to four"* with the last fifty movements and lets the reader believe that is all of
   them. The count is printed and the pager is drawn against it.
   ========================================================================= */

export function Ledger({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [itemId, setItemId] = useState('')
  const [locationId, setLocationId] = useState('')
  const [skip, setSkip] = useState(0)

  /* **EITHER GRANT OPENS IT**, the same pair the server names: `stock.move` is the store
     keeper's and `stock.adjust` is the manager's, and the seed gives the manager only the
     second. One grant would shut out one of the two people the ledger exists for. */
  const mayRead = may('stock.move') || may('stock.adjust')

  const page = useQuery({
    queryKey: ['ledger', propertyId, itemId, locationId, skip],
    queryFn: ({ signal }) => fetchLedger(
      propertyId,
      {
        ...(itemId ? { itemId } : {}),
        ...(locationId ? { locationId } : {}),
        take: PAGE, skip,
      },
      { signal }),
    enabled: mayRead,
    /* **THE OLD PAGE STAYS UP WHILE THE NEXT LOADS.** A ledger that blanks to a spinner
       on every filter change is a screen somebody loses their place in. */
    placeholderData: keepPreviousData,
    retry: false,
  })

  if (!mayRead) return <NoPermissionState />
  if (page.isLoading && !page.error) return <LoadingState rows={6} />

  if (page.error && !page.data) {
    if (page.error instanceof ApiError && page.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(page.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={page.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  const view = page.data!
  const shownFrom = view.total === 0 ? 0 : skip + 1
  const shownTo = Math.min(skip + view.rows.length, view.total)

  /* A filter change has to go back to the first page: leaving `skip` at 150 on a filter
     with twelve rows is an empty screen that reads as "there is nothing". */
  const filterTo = (next: () => void) => { next(); setSkip(0) }

  return (
    <div className="space-y-4 p-4">
      <Card>
        <CardHeader title={t.lgTitle} icon={<History className="size-4" />} />

        <CardBody className="space-y-2 border-b border-border">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-[12px]">
              {t.lgItem}
              <select
                value={itemId}
                onChange={(e) => filterTo(() => setItemId(e.target.value))}
                aria-label={t.lgItem}
                className="rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              >
                <option value="">{t.lgAllItems}</option>
                {view.items.map((i) => (
                  <option key={i.stockItemId} value={i.stockItemId}>
                    {nameIn(locale, i.name, i.nameAr)}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-1 text-[12px]">
              {t.lgPlace}
              <select
                value={locationId}
                onChange={(e) => filterTo(() => setLocationId(e.target.value))}
                aria-label={t.lgPlace}
                className="rounded-hk border border-border bg-background px-2 py-1 text-[13px]"
              >
                <option value="">{t.lgAnywhere}</option>
                {view.places.map((p) => (
                  <option key={p.stockLocationId} value={p.stockLocationId}>
                    {nameIn(locale, p.name, p.nameAr)}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* **EVERYTHING THAT TOUCHED IT, IN EITHER DIRECTION.** Said out loud, because a
              place filter that meant "out of" would quietly answer a different question. */}
          {locationId !== '' && (
            <p className="text-[12px] text-muted-foreground">{t.lgEitherDirection}</p>
          )}
        </CardBody>

        {view.rows.length === 0 ? (
          <CardBody>
            <EmptyState title={t.lgNothing} body={t.lgNothingBody} />
          </CardBody>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border text-[11.5px] text-muted-foreground">
                  <th className="px-3 py-2 text-start font-medium">{t.lgWhen}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.lgWhat}</th>
                  <th className="px-3 py-2 text-end font-medium">{t.lgHowMany}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.lgWhereFrom}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.lgWhereTo}</th>
                  <th className="px-3 py-2 text-start font-medium">{t.lgWho}</th>
                </tr>
              </thead>
              <tbody>
                {view.rows.map((row) => <Movement key={row.stockMovementId} row={row} />)}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="flex flex-wrap items-center justify-between gap-2 text-[12px]">
        {/* **HOW MANY THERE ACTUALLY ARE.** The figure that stops fifty rows being read
            as the whole history of a towel. */}
        <span className="text-muted-foreground">
          {fill(t.lgShowing, {
            from: formatCount(shownFrom, locale),
            to: formatCount(shownTo, locale),
            total: formatCount(view.total, locale),
          })}
        </span>

        <span className="flex items-center gap-2">
          <button
            type="button"
            disabled={skip === 0}
            onClick={() => setSkip(Math.max(0, skip - PAGE))}
            className="rounded-hk border border-border px-2 py-1 disabled:opacity-40"
          >
            {t.lgNewer}
          </button>
          <button
            type="button"
            disabled={shownTo >= view.total}
            onClick={() => setSkip(skip + PAGE)}
            className="rounded-hk border border-border px-2 py-1 disabled:opacity-40"
          >
            {t.lgOlder}
          </button>
        </span>
      </div>

      <p className="text-[12px] text-muted-foreground">{t.lgNote}</p>
    </div>
  )
}

function Movement({ row }: { row: LedgerRow }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  /* **AN ADJUSTMENT IS THE ONLY SIGNED QUANTITY**, and its sign is the correction itself.
     Everything else carries its direction in from/to, so drawing a minus on a transfer
     would be inventing a second way to say the same thing. */
  const signed = row.kind === 'ADJUSTMENT'

  return (
    <tr className="border-b border-border/60 last:border-0 align-top">
      <td className="px-3 py-2 whitespace-nowrap text-muted-foreground">
        {formatDateTime(new Date(row.occurredAtUtc), locale)}
      </td>

      <td className="px-3 py-2">
        <span className="block font-medium">{t.lgKinds[row.kind]}</span>
        <span className="block text-[11px] text-muted-foreground">
          <span dir="ltr">{row.itemCode}</span>
          {/* A count's own correction is not somebody adjusting a figure on a Tuesday. */}
          {row.stockCountId && <>{' · '}{t.lgFromACount}</>}
        </span>
        {/* **THE REASON IS DRAWN AS IT WAS TYPED AND NEVER TRANSLATED**, the same rule a
            fault's description follows: a written-off towel's explanation can become
            evidence, and running it through a translator is how it stops being. */}
        {row.reason && (
          <span className="mt-0.5 block text-[12px]"><Bidi>{row.reason}</Bidi></span>
        )}
      </td>

      <td className="px-3 py-2 text-end tnum whitespace-nowrap">
        {signed && row.quantity > 0 ? '+' : ''}
        {formatQuantity(row.quantity, locale)}
      </td>

      <td className="px-3 py-2 text-muted-foreground">
        {row.fromCode ? <span dir="ltr">{row.fromCode}</span> : <span aria-hidden>—</span>}
      </td>

      <td className="px-3 py-2 text-muted-foreground">
        {row.toCode
          ? <span dir="ltr">{row.toCode}</span>
          : row.roomNumber
            /* **A ROOM IS A DESTINATION TOO.** An issue has no `to` place, and drawing a
               dash there would say the towels went nowhere. */
            ? <span dir="ltr">{fill(t.lgToRoom, { room: row.roomNumber })}</span>
            : <span aria-hidden>—</span>}
      </td>

      <td className="px-3 py-2 text-muted-foreground">
        <Bidi>{nameIn(locale, row.actorName, row.actorNameAr)}</Bidi>
      </td>
    </tr>
  )
}
